import { useState, useCallback, useRef, useEffect } from "react";
import { useNodesState, useEdgesState, addEdge } from "@xyflow/react";
import { setDiagramClipboard } from "../diagramClipboardStore";

// Limite máximo de snapshots mantidos na pilha de histórico de undo e redo
const MAX_HISTORY = 30;

// Extrai o diagrama que já veio junto com os dados do circuito
export const extrairDiagramaInicial = (data) => ({
  nodes: data?.diagramaEquipamentos?.nodes || [],
  edges: data?.diagramaEquipamentos?.edges || [],
});

// Hook para gerenciar estado de nós, arestas, seleção, histórico e conexões do diagrama
export default function useDiagramaState({
  data,
  diagramInstanceKey = "",
  editMode = false,
  onResetAnexos,
  onCapturarSnapshotExtras,
  onRestaurarSnapshotExtras,
  onColar,
}) {
  // Estado inicial dos nós e arestas extraídos dos dados do circuito
  const [dadosIniciais] = useState(() => extrairDiagramaInicial(data));

  // Estado dos nós e callback base de alteração do React Flow
  const [nodes, setNodes, onNodesChange] = useNodesState(
    dadosIniciais?.nodes || [],
  );

  // Estado das arestas e callback de alteração do React Flow
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    dadosIniciais?.edges || [],
  );

  // Identificador do nó atualmente selecionado
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  // Identificador da aresta atualmente selecionada
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);

  // Pilha de histórico para desfazer (undo)
  const historyRef = useRef([]);

  // Pilha de histórico para refazer (redo)
  const redoRef = useRef([]);

  // Controla captura única de histórico durante um arrasto de node
  const dragStartSnapshotRef = useRef(false);

  // Controla captura única de histórico por sessão de edição de node
  const nodeEditSnapshotRef = useRef(null);

  // Controla captura única de histórico por sessão de edição de edge
  const edgeEditSnapshotRef = useRef(null);

  // Monta um snapshot do estado atual para a pilha de histórico
  const capturarSnapshotAtual = useCallback(() => {
    const extras = onCapturarSnapshotExtras?.() || {};
    return {
      nodes: (nodes || []).map((n) => ({ ...n })),
      edges: (edges || []).map((e) => ({ ...e })),
      ...extras,
    };
  }, [nodes, edges, onCapturarSnapshotExtras]);

  // Empilha o estado atual no histórico antes de uma mutação
  const pushHistory = useCallback(() => {
    historyRef.current = [
      capturarSnapshotAtual(),
      ...historyRef.current.slice(0, MAX_HISTORY - 1),
    ];
    redoRef.current = [];
  }, [capturarSnapshotAtual]);

  // Desfaz a última ação restaurando o snapshot anterior do histórico
  const handleUndo = useCallback(() => {
    if (!editMode || historyRef.current.length === 0) return;
    const [ultima, ...restante] = historyRef.current;
    historyRef.current = restante;
    redoRef.current = [
      capturarSnapshotAtual(),
      ...redoRef.current.slice(0, MAX_HISTORY - 1),
    ];
    setNodes(ultima?.nodes || []);
    setEdges(ultima?.edges || []);
    onRestaurarSnapshotExtras?.(ultima);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
  }, [
    editMode,
    capturarSnapshotAtual,
    onRestaurarSnapshotExtras,
    setNodes,
    setEdges,
  ]);

  // Refaz a última ação desfeita restaurando o próximo snapshot do redo
  const handleRedo = useCallback(() => {
    if (!editMode || redoRef.current.length === 0) return;
    const [proxima, ...restante] = redoRef.current;
    redoRef.current = restante;
    historyRef.current = [
      capturarSnapshotAtual(),
      ...historyRef.current.slice(0, MAX_HISTORY - 1),
    ];
    setNodes(proxima?.nodes || []);
    setEdges(proxima?.edges || []);
    onRestaurarSnapshotExtras?.(proxima);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
  }, [
    editMode,
    capturarSnapshotAtual,
    onRestaurarSnapshotExtras,
    setNodes,
    setEdges,
  ]);

  // Cria uma nova conexão entre dois equipamentos ao arrastar entre os handles
  const onConnect = useCallback(
    (params) => {
      pushHistory();
      setEdges((eds) => addEdge(params, eds));
    },
    [setEdges, pushHistory],
  );

  // Intercepta mudanças de nós para capturar histórico no início de cada arrasto
  const handleNodesChange = useCallback(
    (changes) => {
      const iniciouArrasto = (changes || []).some(
        (c) => c?.type === "position" && c?.dragging === true,
      );
      if (iniciouArrasto && !dragStartSnapshotRef.current) {
        pushHistory();
        dragStartSnapshotRef.current = true;
      }
      const finalizouArrasto = (changes || []).some(
        (c) => c?.type === "position" && c?.dragging === false,
      );
      if (finalizouArrasto) {
        dragStartSnapshotRef.current = false;
      }
      onNodesChange(changes);
    },
    [onNodesChange, pushHistory],
  );

  // Retorna nodes e edges sem a flag selected para salvar o diagrama. `style` da edge também sai:
  // é sempre recalculado do tema atual em edgesForCanvas (DiagramaCanvas), nunca deve ser
  // persistido — um hex de tema salvo sobrevive à troca de paleta do tenant e pode virar
  // ilegível (foi assim que o link de backup ficou da cor do fundo no tema escuro)
  const getDiagramData = useCallback(
    () => ({
      nodes: (nodes || []).map(({ selected, ...node }) => node),
      edges: (edges || []).map(({ selected, style, ...edge }) => edge),
    }),
    [nodes, edges],
  );

  // Aplica novo estado de nodes/edges e limpa anexos e histórico
  const resetDiagramData = useCallback(
    (novosNodes, novosEdges) => {
      setNodes(novosNodes || []);
      setEdges(novosEdges || []);
      onResetAnexos?.();
      historyRef.current = [];
      redoRef.current = [];
    },
    [setNodes, setEdges, onResetAnexos],
  );

  // Guarda a referência de data atualizada para leitura fresca na reinicialização
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Guarda o diagramInstanceKey já processado para detectar troca real de instância
  const diagramInstanceKeyRef = useRef(diagramInstanceKey);

  // Reinicializa nodes/edges quando o diagrama por trás do mesmo circuito muda sem o componente
  // remontar (Task 5 da aplicação em massa: reaplicar template num circuito já aberto não troca o
  // id do circuito, só o diagrama salvo). dadosIniciais só captura `data` uma vez, na montagem, então
  // sem este efeito o canvas ficaria preso no diagrama antigo até uma troca de circuito de verdade
  useEffect(() => {
    if (diagramInstanceKeyRef.current === diagramInstanceKey) return;
    diagramInstanceKeyRef.current = diagramInstanceKey;
    const dadosAtualizados = extrairDiagramaInicial(dataRef.current || data);
    resetDiagramData(dadosAtualizados?.nodes, dadosAtualizados?.edges);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só reage à troca do identificador da instância; data/resetDiagramData mudam a cada render e reintroduziriam o efeito toda hora
  }, [diagramInstanceKey]);

  // Copia os nós e arestas internas selecionadas para o clipboard compartilhado
  const copySelection = useCallback(() => {
    if (!editMode) return;
    const nodesSelecionados = (nodes || []).filter((node) => node?.selected);
    if (!nodesSelecionados?.length) return;
    const idsSelecionados = new Set(nodesSelecionados.map((node) => node?.id));
    const edgesInternas = (edges || []).filter(
      (edge) =>
        idsSelecionados.has(edge?.source) && idsSelecionados.has(edge?.target),
    );
    setDiagramClipboard({
      nodes: nodesSelecionados.map(({ selected, ...node }) => ({ ...node })),
      edges: edgesInternas.map(({ selected, ...edge }) => ({ ...edge })),
    });
  }, [editMode, nodes, edges]);

  // Cola a seleção a partir do clipboard compartilhado
  const colarClipboard = useCallback(() => {
    if (!editMode) return;
    onColar?.();
  }, [editMode, onColar]);

  // Seleciona todos os nós e conexões do diagrama
  const selectAll = useCallback(() => {
    if (!editMode) return;
    setNodes((prev) => (prev || []).map((node) => ({ ...node, selected: true })));
    setEdges((prev) => (prev || []).map((edge) => ({ ...edge, selected: true })));
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
  }, [editMode, setNodes, setEdges]);

  // Limpa a seleção do painel caso o nó selecionado tenha sido removido
  useEffect(() => {
    if (selectedNodeId && !nodes?.some((node) => node?.id === selectedNodeId)) {
      setSelectedNodeId(null);
    }
  }, [nodes, selectedNodeId]);

  // Limpa a seleção do painel caso a conexão selecionada tenha sido removida
  useEffect(() => {
    if (selectedEdgeId && !edges?.some((edge) => edge?.id === selectedEdgeId)) {
      setSelectedEdgeId(null);
    }
  }, [edges, selectedEdgeId]);

  // Desmarca a seleção interna do React Flow ao sair do modo de edição
  useEffect(() => {
    if (editMode) return;
    setNodes((prev) =>
      (prev || []).some((node) => node?.selected)
        ? prev.map((node) =>
            node?.selected ? { ...node, selected: false } : node,
          )
        : prev,
    );
    setEdges((prev) =>
      (prev || []).some((edge) => edge?.selected)
        ? prev.map((edge) =>
            edge?.selected ? { ...edge, selected: false } : edge,
          )
        : prev,
    );
  }, [editMode, setNodes, setEdges]);

  // Reseta a ref de sessão de edição de node ao trocar de node selecionado
  useEffect(() => {
    nodeEditSnapshotRef.current = null;
  }, [selectedNodeId]);

  // Reseta a ref de sessão de edição de edge ao trocar de edge selecionada
  useEffect(() => {
    edgeEditSnapshotRef.current = null;
  }, [selectedEdgeId]);

  // Libera a flag de snapshot de arrasto no evento global de pointerup
  useEffect(() => {
    if (!editMode) return undefined;
    const resetDragSnapshotFlag = () => {
      dragStartSnapshotRef.current = false;
    };
    window?.addEventListener?.("pointerup", resetDragSnapshotFlag);
    return () => window?.removeEventListener?.("pointerup", resetDragSnapshotFlag);
  }, [editMode]);

  return {
    nodes,
    setNodes,
    edges,
    setEdges,
    onNodesChange: handleNodesChange,
    handleNodesChange,
    onEdgesChange,
    onConnect,
    selectedNodeId,
    selectedEdgeId,
    setSelectedNodeId,
    setSelectedEdgeId,
    historyRef,
    redoRef,
    dragStartSnapshotRef,
    nodeEditSnapshotRef,
    edgeEditSnapshotRef,
    pushHistory,
    handleUndo,
    handleRedo,
    getDiagramData,
    resetDiagramData,
    copySelection,
    copiarSelecao: copySelection,
    colarClipboard,
    pasteClipboard: colarClipboard,
    selectAll,
  };
}
